import { useState, useEffect, useRef, useCallback } from 'react';

// Hook for micro-interactions and subtle animations
export const useMicroInteractions = () => {
  const [interactions, setInteractions] = useState({
    hover: false,
    active: false,
    focus: false,
    ripple: null
  });

  const elementRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    setInteractions(prev => ({ ...prev, hover: true }));
  }, []);

  const handleMouseLeave = useCallback(() => {
    setInteractions(prev => ({ ...prev, hover: false, active: false }));
  }, []);

  const handleMouseDown = useCallback((e) => {
    setInteractions(prev => ({ ...prev, active: true }));
    
    // Create ripple effect
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      const ripple = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        id: Date.now()
      };
      setInteractions(prev => ({ ...prev, ripple }));
      
      // Remove ripple after animation
      setTimeout(() => {
        setInteractions(prev => ({ ...prev, ripple: null }));
      }, 600);
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    setInteractions(prev => ({ ...prev, active: false }));
  }, []);

  const handleFocus = useCallback(() => {
    setInteractions(prev => ({ ...prev, focus: true }));
  }, []);

  const handleBlur = useCallback(() => {
    setInteractions(prev => ({ ...prev, focus: false }));
  }, []);

  const bindEvents = useCallback((element) => {
    if (!element) return;
    
    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);
    element.addEventListener('mousedown', handleMouseDown);
    element.addEventListener('mouseup', handleMouseUp);
    element.addEventListener('focus', handleFocus);
    element.addEventListener('blur', handleBlur);

    return () => {
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
      element.removeEventListener('mousedown', handleMouseDown);
      element.removeEventListener('mouseup', handleMouseUp);
      element.removeEventListener('focus', handleFocus);
      element.removeEventListener('blur', handleBlur);
    };
  }, [handleMouseEnter, handleMouseLeave, handleMouseDown, handleMouseUp, handleFocus, handleBlur]);

  useEffect(() => {
    if (elementRef.current) {
      return bindEvents(elementRef.current);
    }
  }, [bindEvents]);

  return {
    elementRef,
    interactions,
    bindEvents
  };
};

// Hook for hover tilt effect
export const useHoverTilt = (intensity = 10) => {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const elementRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!elementRef.current) return;

    const rect = elementRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const deltaX = (e.clientX - centerX) / (rect.width / 2);
    const deltaY = (e.clientY - centerY) / (rect.height / 2);
    
    setTilt({
      x: deltaY * intensity,
      y: deltaX * intensity
    });
  }, [intensity]);

  const handleMouseLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseMove, handleMouseLeave]);

  return {
    elementRef,
    tilt,
    transform: `perspective(1000px) rotateX(${-tilt.x}deg) rotateY(${tilt.y}deg)`
  };
};

// Hook for magnetic cursor effect
export const useMagneticEffect = (strengthPercent = 30, distance = 100) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const elementRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const strength =
    typeof strengthPercent === "number"
      ? strengthPercent / 100
      : Number(strengthPercent) / 100;

  const handleMouseMove = useCallback((e) => {
    if (!elementRef.current) return;

    const rect = elementRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const deltaX = e.clientX - centerX;
    const deltaY = e.clientY - centerY;
    const dist = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    if (dist < distance) {
      setIsHovering(true);
      setPosition({
        x: deltaX * strength,
        y: deltaY * strength
      });
    } else {
      setIsHovering(false);
      setPosition({ x: 0, y: 0 });
    }
  }, [strength, distance]);

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    setPosition({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    
    const element = elementRef.current;
    if (element) {
      element.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (element) {
        element.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [handleMouseMove, handleMouseLeave]);

  return {
    elementRef,
    position,
    isHovering,
    transform: `translate3d(${position.x}px, ${position.y}px, 0)`
  };
};

// Hook for morphing shapes
export const useMorphingShape = (shapes = ['circle', 'square'], duration = 2000) => {
  const [currentShape, setCurrentShape] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (shapes.length <= 1) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentShape(prev => (prev + 1) % shapes.length);
        setIsAnimating(false);
      }, 300);
    }, duration);

    return () => clearInterval(interval);
  }, [shapes, duration]);

  const getShapeStyle = (shape) => {
    const baseStyle = {
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
      transform: isAnimating ? 'scale(1.1)' : 'scale(1)'
    };

    switch (shape) {
      case 'circle':
        return { ...baseStyle, borderRadius: '50%' };
      case 'square':
        return { ...baseStyle, borderRadius: '0%' };
      case 'rounded':
        return { ...baseStyle, borderRadius: '20%' };
      case 'pill':
        return { ...baseStyle, borderRadius: '50px' };
      default:
        return baseStyle;
    }
  };

  return {
    currentShape: shapes[currentShape],
    shapeStyle: getShapeStyle(shapes[currentShape]),
    isAnimating
  };
};

// Hook for floating animation
export const useFloatingAnimation = (duration = 3000, intensity = 10) => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    let startTime = Date.now();
    let animationFrame;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = (elapsed % duration) / duration;
      const newOffset = Math.sin(progress * 2 * Math.PI) * intensity;
      setOffset(newOffset);
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [duration, intensity]);

  return {
    transform: `translateY(${offset}px)`,
    offset
  };
};

// Hook for pulse effect
export const usePulseEffect = (scale = 1.05, duration = 1000) => {
  const [isPulsing, setIsPulsing] = useState(false);

  const startPulse = useCallback(() => {
    setIsPulsing(true);
    setTimeout(() => setIsPulsing(false), duration);
  }, [duration]);

  return {
    isPulsing,
    startPulse,
    pulseStyle: {
      transform: isPulsing ? `scale(${scale})` : 'scale(1)',
      transition: `transform ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`
    }
  };
};

// Hook for stagger animation
export const useStaggerAnimation = (delay = 100, trigger = true) => {
  const [visibleItems, setVisibleItems] = useState(new Set());

  useEffect(() => {
    if (!trigger) {
      setVisibleItems(new Set());
      return;
    }

    const intervals = [];
    
    // Stagger animation for multiple items
    for (let i = 0; i < 10; i++) {
      const timeout = setTimeout(() => {
        setVisibleItems(prev => new Set([...prev, i]));
      }, i * delay);
      intervals.push(timeout);
    }

    return () => {
      intervals.forEach(clearTimeout);
    };
  }, [delay, trigger]);

  const getItemStyle = (index) => ({
    opacity: visibleItems.has(index) ? 1 : 0,
    transform: visibleItems.has(index) ? 'translateY(0)' : 'translateY(20px)',
    transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
  });

  return {
    visibleItems,
    getItemStyle,
    reset: () => setVisibleItems(new Set())
  };
};

// Hook for button interactions
export const useButtonInteractions = () => {
  const [state, setState] = useState({
    isPressed: false,
    isHovered: false,
    ripples: []
  });

  const addRipple = useCallback((e, element) => {
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    
    const newRipple = {
      id: Date.now(),
      x,
      y,
      size
    };

    setState(prev => ({
      ...prev,
      ripples: [...prev.ripples, newRipple]
    }));

    // Remove ripple after animation
    setTimeout(() => {
      setState(prev => ({
        ...prev,
        ripples: prev.ripples.filter(ripple => ripple.id !== newRipple.id)
      }));
    }, 600);
  }, []);

  const handleMouseDown = useCallback((e) => {
    setState(prev => ({ ...prev, isPressed: true }));
    addRipple(e, e.currentTarget);
  }, [addRipple]);

  const handleMouseUp = useCallback(() => {
    setState(prev => ({ ...prev, isPressed: false }));
  }, []);

  const handleMouseEnter = useCallback(() => {
    setState(prev => ({ ...prev, isHovered: true }));
  }, []);

  const handleMouseLeave = useCallback(() => {
    setState(prev => ({ ...prev, isHovered: false, isPressed: false }));
  }, []);

  return {
    state,
    handlers: {
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave
    }
  };
};

const MicroInteractionHooks = {
  useMicroInteractions,
  useHoverTilt,
  useMagneticEffect,
  useMorphingShape,
  useFloatingAnimation,
  usePulseEffect,
  useStaggerAnimation,
  useButtonInteractions
};

export default MicroInteractionHooks;
